import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Edit2, Plus, Trash2, Printer, Download } from 'lucide-react';

const JOURS_SEMAINE = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'];
const JOURS_WEEKEND = ['samedi', 'dimanche'];
const HORAIRES_NORMAL = [
  { index: 0, label: '09:00-10:30' },
  { index: 1, label: '10:45-12:15' },
  { index: 2, label: '13:15-14:45' },
  { index: 3, label: '15:00-16:30' }
];
// TA : soiree en semaine, journee le week-end. Un cours de 3h occupe deux creneaux
// consecutifs et part forcement de 19:00, 09:30 ou 13:30.
const HORAIRES_TA = [
  { index: 0, label: '19:00-20:30' },
  { index: 1, label: '20:30-22:00' },
  { index: 2, label: '09:30-11:00' },
  { index: 3, label: '11:00-12:30' },
  { index: 4, label: '13:30-15:00' },
  { index: 5, label: '15:00-16:30' }
];

const normaliserRegime = (r) => (String(r || '').toUpperCase() === 'TA' ? 'TA' : 'NORMAL');
const regimeDeClasse = (c) => normaliserRegime(c && (c.regime || c.regime_classe));

const creneauValide = (regime, jour, periode, duree) => {
  const p = parseInt(periode, 10);
  const d = parseInt(duree, 10) || 1;
  if (normaliserRegime(regime) !== 'TA') {
    return (JOURS_SEMAINE.includes(jour) || jour === 'samedi') && p >= 0 && p <= 3 && (d === 1 || (d === 2 && p <= 2));
  }
  if (d !== 1 && d !== 2) return false;
  if (JOURS_SEMAINE.includes(jour)) {
    if (p !== 0 && p !== 1) return false;
    if (d === 2 && p !== 0) return false;
    return true;
  }
  if (JOURS_WEEKEND.includes(jour)) {
    if (p < 2 || p > 5) return false;
    if (d === 2 && p !== 2 && p !== 4) return false;
    return true;
  }
  return false;
};

const sansAccents = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const compact = (s) => sansAccents(s).replace(/[^a-z0-9]/g, '');

const MOTS_VIDES = ['en', 'de', 'du', 'des', 'la', 'le', 'et', 'option', 'annee', 'annees', 'master', 'mastere', 'semestre', 'stages', 'filiere'];

// "L1 IRM" -> "irm1", "1CPI" -> "cpi1" : permet de joindre un groupe a son niveau
const variantesNom = (nom) => {
  const out = new Set();
  const base = compact(nom);
  if (base) out.add(base);
  const toks = String(nom || '').split(/[^A-Za-z0-9]+/).filter(Boolean);
  const tokNiveau = toks.find(t => /^\d$/.test(t) || /^[a-z]\d$/i.test(t) || /^\d[a-z]$/i.test(t));
  const chiffre = (tokNiveau || '').match(/\d/);
  const champ = toks.filter(t => t !== tokNiveau).join('');
  if (champ && chiffre) {
    out.add(compact(champ + chiffre[0]));
    out.add(compact(champ + 'n' + chiffre[0]));
  }
  return [...out].filter(Boolean);
};

// "L3 Entrepreneuriat Audit & Finance" -> "eaf"
const initialesNom = (nom) =>
  sansAccents(nom)
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .filter(t => !/[0-9]/.test(t) && !MOTS_VIDES.includes(t) && t !== 'l')
    .map(t => t[0])
    .join('');

// "L1" / "M2" : sert a ne pas confondre un niveau de licence avec un niveau master
const jetonNiveau = (nom) => {
  const tok = String(nom || '')
    .split(/[^A-Za-z0-9]+/)
    .find(t => /^(l|m)\d$/i.test(t) || /^\d(l|m)$/i.test(t));
  return tok ? tok.toLowerCase() : '';
};

// "MASI" est-il une sous-suite des mots du groupe ? (sigle derive des mots)
const estSousSuite = (court, long) => {
  let i = 0;
  for (const c of long) {
    if (c === court[i]) i += 1;
    if (i === court.length) return true;
  }
  return i === court.length;
};

const scoreNiveau = (niveau, nomClasse) => {
  const intitule = (niveau && (niveau.intitule || niveau.nom || niveau.code)) || '';
  const nn = compact(intitule);
  if (!nn) return 0;
  const vs = variantesNom(nomClasse);
  let score = 0;
  if (vs.includes(nn)) score = 100;
  else if (vs.some(v => v.length >= 4 && nn.length >= 4 && (nn.startsWith(v) || v.startsWith(nn)))) score = 80;
  const tokensNiveau = sansAccents(intitule)
    .split(/[^a-z0-9]+/)
    .filter(t => t.length >= 2 && !/^\d/.test(t));
  // un sigle qui tombe exactement sur un mot du niveau est plus discriminant qu'un simple prefixe :
  // "L3 Developpement Commercial & Marketing Digital" doit tomber sur L3 DCMD, pas sur L3 Dev
  const ini = initialesNom(nomClasse);
  const motsUtiles = tokensNiveau.filter(t => !MOTS_VIDES.includes(t));
  if (ini.length >= 3 && motsUtiles.includes(ini)) score = Math.max(score, 90);
  else if (score === 0 && ini.length >= 3 && motsUtiles.some(t => t.startsWith(ini) || ini.startsWith(t))) score = 50;
  if (score === 0) {
    const sigle = tokensNiveau.find(t => t.length >= 3 && !MOTS_VIDES.includes(t) && !/[0-9]/.test(t));
    if (sigle && estSousSuite(sigle, sansAccents(nomClasse).replace(/[^a-z0-9]/g, ''))) score = 40;
  }
  const jc = jetonNiveau(nomClasse);
  const jn = jetonNiveau(intitule);
  if (jc && jn && jc !== jn) score -= 30;
  return score;
};

const EmploiTemps = () => {
  const [classes, setClasses] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [professeurs, setProfesseurs] = useState([]);
  const [salles, setSalles] = useState([]);
  const [cards, setCards] = useState([]);
  const [lecons, setLecons] = useState([]);
  
  const [selectedClasse, setSelectedClasse] = useState('');
  const [emploiData, setEmploiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingCours, setEditingCours] = useState(null);
  const [filtreMatiereActif, setFiltreMatiereActif] = useState(true);
  const [filtreGroupeMatiere, setFiltreGroupeMatiere] = useState('');
  const [filtreFiliereMatiere, setFiltreFiliereMatiere] = useState('');
  const [filtreNiveauMatiere, setFiltreNiveauMatiere] = useState('');
  const [filtreSemestreMatiere, setFiltreSemestreMatiere] = useState('all');
  const [periodes, setPeriodes] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [modules, setModules] = useState([]);
  const [filiereNiveaux, setFiliereNiveaux] = useState([]);
  const [anneeActive, setAnneeActive] = useState('');
  // classes : liste des classes regroupées sur la même séance. Une seule entrée = séance
  // normale, plusieurs = cours commun (la direction peut regrouper CPI1 et IRM, par exemple).
  const [newCours, setNewCours] = useState({
    jour: 'lundi',
    periode: 0,
    matiereId: '',
    matieresParClasse: {},
    professeurId: '',
    salleId: '',
    duree: 1,
    classes: []
  });

  const normalizeText = (str = '') => String(str).toLowerCase().replace(/\s+/g, '').trim();

  // Le regime vient de la classe selectionnee : une classe TA n affiche jamais la grille
  // normale, et inversement. Les anciennes classes sans regime restent NORMAL.
  const classeSelectionnee = classes.find(c => (c.name || c.nom) === selectedClasse);
  const regime = regimeDeClasse(classeSelectionnee);
  const estTA = regime === 'TA';
  const jours = estTA ? [...JOURS_SEMAINE, ...JOURS_WEEKEND] : [...JOURS_SEMAINE];
  const joursLabels = jours.map(j => j.charAt(0).toUpperCase() + j.slice(1));
  const horaires = estTA ? HORAIRES_TA : HORAIRES_NORMAL;
  const horairesFormulaire = !estTA
    ? HORAIRES_NORMAL
    : (JOURS_WEEKEND.includes(newCours.jour)
      ? HORAIRES_TA.filter(h => h.index >= 2)
      : HORAIRES_TA.filter(h => h.index <= 1));

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isEnseignant = user?.role === 'ENSEIGNANT';
  const isEtudiant = user?.role === 'ETUDIANT';
  // Le chef de filière gère l'emploi du temps comme l'administrateur : ajout,
  // modification et suppression de cours.
  const peutEditerEmploi = ['ADMIN', 'CHEF_FILIERE'].includes(user?.role);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if (selectedClasse && lecons.length > 0 && cards.length > 0) {
      generateEmploiTemps();
    }
  }, [selectedClasse, lecons, cards, matieres, professeurs, salles]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [classesRes, matieresRes, profsRes, sallesRes, cardsRes, leconsRes, periodesRes, filieresRes, niveauxRes, modulesRes, fnRes, anneesRes] = await Promise.all([
        axios.get('/api/emploi/classes', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/matieres', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/professeurs', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/salles', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/cards', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/emploi/lecons', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/periodes', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/filieres', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/niveaux', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/modules', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/filiere-niveaux', { headers, timeout: 8000 }).catch(() => ({ data: [] })),
        axios.get('/api/annees', { headers, timeout: 8000 }).catch(() => ({ data: [] }))
      ]);

      const classesNettoyees = dedoublonnerParId((classesRes.data || []).map(c => ({ ...c, id: c.id || c._id, name: c.name || c.nom })));
      const periodesNettoyees = dedoublonnerParId(periodesRes.data || []);
      const matieresNettoyees = (matieresRes.data || []).map(m => ({ ...m, id: m.id || m._id, name: m.name || m.nom }));
      // les niveaux et periodes sont croises entre eux : on dedoublonne avant d installer
      // les etats, sinon la comparaison niveau_id / _id perd des periodes au passage
      const annees = anneesRes.data && anneesRes.data.data ? anneesRes.data.data : (anneesRes.data || []);
      const active = Array.isArray(annees) ? (annees.find(a => a.estActive) || null) : null;
      const anneeActiveChargee = active ? String(active.annee_id || active._id || active.id || '') : '';
      // Les niveaux sont dedoublonnes avant l installation des etats, donc avant que
      // anneeActive ne soit positionnee : le tri par annee doit utiliser la valeur
      // calculee ici, sinon la copie 2027 etait ecartee au profit de l annee precedente.
      const niveauxNettoyes = dedoublonnerNiveaux(niveauxRes.data || [], classesNettoyees, periodesNettoyees, matieresNettoyees, anneeActiveChargee);
      setClasses(classesNettoyees);
      setMatieres(matieresNettoyees);
      setProfesseurs(profsRes.data || []);
      setSalles(sallesRes.data || []);
      setCards(cardsRes.data || []);
      setLecons(leconsRes.data || []);
      setPeriodes(periodesNettoyees);
      setFilieres(filieresRes.data || []);
      setNiveaux(niveauxNettoyes);
      setModules(modulesRes.data || []);
      setFiliereNiveaux(fnRes.data || []);
      setAnneeActive(anneeActiveChargee);

      if (isEnseignant) {
        const allClassess = classesRes.data || [];
        const allProfs = profsRes.data || [];
        const allLecons = leconsRes.data || [];
        const matches = normalizeText(user.email);
        const profMaj = allProfs.find(p =>
          (p.email && normalizeText(p.email) === matches) ||
          normalizeText(`${p.name} ${p.short || ''}`).includes(normalizeText(user.nom || '')) ||
          normalizeText(`${user.nom || ''} ${user.prenom || ''}`).toLowerCase() === normalizeText(p.name).toLowerCase()
        );
        const profId = profMaj?.id || '';
        const leconsProf = profId
          ? allLecons.filter(l => l.teacherId === profId)
          : [];
        // meme raison que plus bas : on compare des chaines pour ignorer la forme du stockage
        const classIdsProf = new Set(
          leconsProf.flatMap(l => (l.classIds || []).map(refId).filter(Boolean))
        );
        const classesProf = classIdsProf.size > 0
          ? allClassess.filter(c => classIdsProf.has(classeId(c)))
          : [];
        setLecons(leconsProf);
        setClasses(classesProf);
        if (classesProf.length > 0) {
          setSelectedClasse(classesProf[0].name);
        } else {
          setSelectedClasse('');
        }
        setError(classesProf.length === 0 ? 'Aucun cours assigné à votre nom dans l\'emploi du temps' : '');
      } else {
        if (isEtudiant && classesRes.data && classesRes.data.length > 0) {
          try {
            let classeTrouvee = null;
            // Priorite: endpoint espace etudiant
            const emploiEtudRes = await axios.get('/api/etudiant/emploi', { headers, timeout: 8000 }).catch(() => ({ data: null }));
            if (emploiEtudRes.data) {
              const c = emploiEtudRes.data.classe || emploiEtudRes.data.classe_etudiant || null;
              if (c) {
                const cid = c._id || c.id;
                if (cid) classeTrouvee = classesRes.data.find(cl => String(cl._id) === String(cid) || String(cl.id) === String(cid));
                if (!classeTrouvee && (c.nom || c.name)) classeTrouvee = classesRes.data.find(cl => (cl.name || cl.nom) === (c.nom || c.name));
              }
              if (!classeTrouvee && emploiEtudRes.data.classe_nom) {
                classeTrouvee = classesRes.data.find(cl => (cl.name || cl.nom) === emploiEtudRes.data.classe_nom);
              }
            }
            if (!classeTrouvee) {
              const insRes = await axios.get('/api/inscriptions/etudiant/' + (user._id || user.id || user.etudiant_id), { headers, timeout: 8000 }).catch(() => ({ data: null }));
              if (insRes.data) {
                const ins = Array.isArray(insRes.data) ? insRes.data[0] : insRes.data;
                if (ins) {
                  const cid = ins.classe_id || ins.classe?._id || ins.classe?.id;
                  if (cid) classeTrouvee = classesRes.data.find(c => String(c._id) === String(cid) || String(c.id) === String(cid));
                  if (!classeTrouvee && ins.classe_nom) classeTrouvee = classesRes.data.find(c => (c.name || c.nom) === ins.classe_nom);
                }
              }
            }
            if (!classeTrouvee) {
              const etuRes = await axios.get('/api/etudiants/' + (user.etudiant_id || user.id || user._id), { headers, timeout: 8000 }).catch(() => ({ data: null }));
              if (etuRes.data) {
                const etu = etuRes.data;
                const cid = etu.classe_id || etu.classe?._id || etu.classe_id_etudiant;
                if (cid) classeTrouvee = classesRes.data.find(c => String(c._id) === String(cid) || String(c.id) === String(cid));
                if (!classeTrouvee && (etu.classe_nom || etu.classe?.nom || etu.classe?.name)) classeTrouvee = classesRes.data.find(c => (c.name || c.nom) === (etu.classe_nom || etu.classe?.nom || etu.classe?.name));
              }
            }
            if (classeTrouvee) {
              setClasses([classeTrouvee]);
              setSelectedClasse(classeTrouvee.name || classeTrouvee.nom || '');
            } else if (classesRes.data && classesRes.data.length > 0) {
              setSelectedClasse(classesRes.data[0].name);
            }
          } catch (e) {
            if (classesRes.data && classesRes.data.length > 0) {
              setSelectedClasse(classesRes.data[0].name);
            }
          }
        } else if (classesRes.data && classesRes.data.length > 0) {
          setSelectedClasse(classesRes.data[0].name);
        }
        setError('');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const generateEmploiTemps = () => {
    const classe = classes.find(c => (c.name || c.nom) === selectedClasse);
    if (!classe) return;
    const regimeClasse = regimeDeClasse(classe);

    // les identifiants de classe sont stockes tantôt en nombre, tantôt en ObjectId :
      // une comparaison directe ferait disappear la seance de l affichage
      const idAffiche = classeId(classe);
      const leconsClasse = lecons.filter(lecon =>
        (lecon.classIds || []).some(x => refId(x) === idAffiche)
      );

    const emploi = [];
    
    for (const card of cards) {
      const lecon = leconsClasse.find(l => l.id === card.lessonId);
      if (!lecon) continue;
      // Une classe TA ne doit pas afficher les cours du regime normal (et inversement).
      if (normaliserRegime(card.regime || lecon.regime) !== regimeClasse) continue;
      
      const matiere = matieres.find(m => m.id === lecon.subjectId);
      const professeur = professeurs.find(p => p.id === lecon.teacherId);
      const salle = salles.find(s => s.id === (card.classroomId || lecon.classroomId));
      
      const daysBit = parseInt(card.days) || 0;
      const joursCours = [];
      if (daysBit & 1) joursCours.push('lundi');
      if (daysBit & 2) joursCours.push('mardi');
      if (daysBit & 4) joursCours.push('mercredi');
      if (daysBit & 8) joursCours.push('jeudi');
      if (daysBit & 16) joursCours.push('vendredi');
      if (daysBit & 32) joursCours.push('samedi');
      if (daysBit & 64) joursCours.push('dimanche');

      const periode = parseInt(card.period);
      const duree = parseInt(card.duree) || 1;
      if (!horaires[periode]) continue;

      for (const jour of joursCours) {
        if (!creneauValide(regimeClasse, jour, periode, duree)) continue;
        emploi.push({
          id: card.lessonId,
          cardId: card._id,
          leconId: lecon.id,
          jour: jour,
          periode: periode,
          duree: duree,
          regime: regimeClasse,
          matiereId: lecon.subjectId,
          matiere: matiere?.name || lecon.subjectName || 'Sans matière',
          // matiere propre a chaque classe quand la seance est commune a plusieurs niveaux
          matieresParClasse: getMatieresDeLecon(lecon).map(x => ({
            ...x,
            classe: (classes.find(c => classeId(c) === x.classId) || {}).name || x.classId,
          })),
          professeurId: lecon.teacherId,
          professeur: professeur?.name || lecon.teacherName || 'Sans professeur',
          salleId: card.classroomId || lecon.classroomId,
          salle: salle?.name || card.classroomName || 'Salle inconnue',
          // toutes les classes de la séance, pas seulement celle affichée : c'est ce qui
          // permet de réouvrir un cours commun et de voir le regroupement
          classIds: lecon.classIds || [],
          classesLiees: (lecon.classIds || [])
            .map(id => {
              const autre = classes.find(x => classeId(x) === String(id));
              return autre ? classeNom(autre) : '';
            })
            .filter(Boolean)
        });
      }
    }
    
    setEmploiData(emploi);
  };

  useEffect(() => {
    if (!showAddModal) return;
    // Quand une seule classe est cochee, son niveau et sa filiere sont deduits : le
    // programmeur n a plus rien a choisir pour filtrer les matieres.
    const choisies = newCours.classes || [];
    if (choisies.length === 1) {
      const c = classes.find(x => classeId(x) === String(choisies[0]));
      setFiltreGroupeMatiere(c ? classeId(c) : '');
      const niv = niveauDeClasse(c);
      setFiltreNiveauMatiere(niv ? getNiveauId(niv) : '');
      const fil = getFiliereDeNiveau(niv);
      setFiltreFiliereMatiere(fil ? getFiliereId(fil) : '');
      const dispo = niv ? periodes.filter(p => memeNiveau(niv, p.niveau_id)) : [];
      // Toutes les periodes du niveau sont proposees : restreindre a une seule masquait les
      // matieres de l autre semestre, ce qui faisait croire que M113 n existait pas.
      setFiltreSemestreMatiere('all');
      return;
    }
    const c = classes.find(x => classeNom(x) === selectedClasse);
    setFiltreGroupeMatiere(c ? classeId(c) : '');
    const niv = niveauDeClasse(c);
    setFiltreNiveauMatiere(niv ? getNiveauId(niv) : '');
    const fil = getFiliereDeNiveau(niv);
    setFiltreFiliereMatiere(fil ? getFiliereId(fil) : '');
    const dispo = niv ? periodes.filter(p => memeNiveau(niv, p.niveau_id)) : [];
    // Toutes les periodes du niveau sont proposees : restreindre a une seule masquait les
      // matieres de l autre semestre, ce qui faisait croire que M113 n existait pas.
      setFiltreSemestreMatiere('all');
  }, [showAddModal, selectedClasse, periodes, niveaux, newCours.classes]);

  // Les identifiants de classe ne sont pas stockes de facon homogene : certaines classes
// portent un _id ObjectId, d autres un id numerique. On renvoie toujours des chaines.
const refId = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'object') {
      // Un ObjectId expose un attribut "id" qui est un buffer binaire, pas un
      // identifiant : le lire produisait une cle illisible, et identique pour plusieurs
      // documents. La deduplication par identifiant ecartait alors des niveaux entiers
      // de la liste, dont la copie 2027 de IRM2 : la page ne pouvait plus afficher que
      // l IRM2 de l annee precedente. On convertsit donc en hexadécimal.
      if (typeof v.toHexString === 'function') return v.toHexString();
      if (v._id !== undefined) return refId(v._id);
      if (v.id !== undefined) return refId(v.id);
      return '';
    }
    return String(v);
  };

  // La base contient deux fois la meme classe (meme id, deux documents) et deux fois le meme
  // niveau sous deux intitules legerement differents. On lit donc des copies dedoublonnees :
  // on garde le premier document de chaque identifiant, sans rien modifier en base.
  const dedoublonnerParId = (liste) => {
    const vus = new Set();
    return (liste || []).filter(item => {
      const cle = refId(item && (item.id !== undefined ? item.id : item._id));
      if (!cle) return true;
      if (vus.has(cle)) return false;
      vus.add(cle);
      return true;
    });
  };

  // Un meme niveau peut apparaitre sous deux codes identiques ("CPI 1" et "1CPI").
  // On garde une seule version, en privilegiant celle dont l intitule correspond le mieux
  // au nom d une classe reelle, sinon celle qui a le plus de matieres.
  // Identifiants de niveaux reellement portes par une classe. Une classe designe son niveau
  // par niveau_id : ce niveau fait autorite et ne doit jamais etre ecarte.
  const niveauxReferencesParUneClasse = (classesConnues) => {
    const refs = new Set();
    (classesConnues || []).forEach(c => {
      [c && c.niveau_id, c && c.niveau, c && c.id, c && c._id].forEach(v => {
        if (v !== undefined && v !== null && v !== '') refs.add(String(v));
      });
    });
    return refs;
  };

  const dedoublonnerNiveaux = (liste, classesConnues, periodeConnue, matieresConnues, anneeRef) => {
    const refsClasses = niveauxReferencesParUneClasse(classesConnues);
    const parCode = new Map();
    dedoublonnerParId(liste).forEach(n => {
      const code = n.code === undefined || n.code === null ? '' : String(n.code).trim();
      if (!code) return;
      if (!parCode.has(code)) parCode.set(code, []);
      parCode.get(code).push(n);
    });
    const sortie = [];
    parCode.forEach(group => {
      if (group.length === 1) { sortie.push(group[0]); return; }
      const notes = group.map(n => {
        const nomId = compact(n.intitule || n.nom || n.code);
        const exact = classesConnues.some(c => compact(c.name || c.nom) === nomId);
        // Une classe qui pointe ce niveau par niveau_id prime sur tout le reste. Sans cette
        // protection, un niveau de l annee anterieure disparu de la liste : L3 Dev existe en
        // niveau 3 (code "3", annee 2025/2026) et en niveau 19 (meme code, annee 2026/2027).
        // Le tri par annee active conservait le 19 et jetait le 3, alors que la classe L3 Dev G1
        // declare niveau_id 3 : elle se retrouvait rattachee au mauvais programme, et la liste
        // de ses matieres venait du niveau 19 au lieu du niveau 3.
        const protege = [n.niveau_id, n._id, n.id].some(v => refsClasses.has(String(v))) ? 1 : 0;
        const anneeCible = anneeRef || anneeActive;
        const deLAnnee = anneeCible && n.annee_id !== undefined && n.annee_id !== null
          && String(n.annee_id) === String(anneeCible) ? 1 : 0;
        const pers = periodeConnue.filter(p => String(p.niveau_id) === String(n.code));
        const modIds = new Set(
          (Array.isArray(n.modules) ? n.modules : []).map(m => refId(m && (m._id || m.id)))
        );
        const nbMat = modIds.size > 0
          ? matieresConnues.filter(m => modIds.has(refId(m.module_id))).length
          : pers.length;
        return { n, exact, protege, deLAnnee, nbMat, pers: pers.length };
      });
      notes.sort((a, b) => (b.protege - a.protege) || (b.exact - a.exact) || (b.deLAnnee - a.deLAnnee) || (b.nbMat - a.nbMat) || (b.pers - a.pers));
      if (notes[0].pers === 0 && notes[1] && notes[1].pers > 0 && notes[0].protege === notes[1].protege) sortie.push(notes[1].n);
      else sortie.push(notes[0].n);
    });
    return sortie;
  };

  // Une seance peut regrouper plusieurs classes. Si la direction n a rien coche, on retombe
  // sur la classe affichee en haut, ce qui conserve le comportement d une seance simple.
  const normaliserClasses = (liste, toutesLesClasses, classeCourante) => {
    const brut = Array.isArray(liste) ? liste : [];
    const retenues = [];
    brut.forEach(id => {
      const cle = refId(id);
      if (!cle || retenues.includes(cle)) return;
      if (!toutesLesClasses.some(c => classeId(c) === cle)) return;
      retenues.push(cle);
    });
    if (retenues.length > 0) return retenues;
    const defaut = toutesLesClasses.find(c => classeNom(c) === classeCourante);
    return defaut ? [classeId(defaut)] : [];
  };

  const classeId = (c) => String(c.id || c._id || '');
  const classeNom = (c) => c.name || c.nom || '';

  const candidatsRef = (v) => {
    const out = new Set();
    const push = (x) => {
      if (x === null || x === undefined) return;
      if (typeof x === 'object') {
        if (x._id) out.add(String(x._id));
        if (x.id) out.add(String(x.id));
        ['nom', 'name', 'intitule', 'libelle', 'label', 'code', 'code_filiere', 'titre'].forEach(k => { if (x[k]) out.add(normalizeText(x[k])); });
      } else {
        out.add(String(x).trim());
        out.add(normalizeText(x));
      }
    };
    if (Array.isArray(v)) v.forEach(push);
    else push(v);
    return out;
  };

  const memeRef = (a, b) => {
    const sa = candidatsRef(a);
    if (sa.size === 0) return false;
    const sb = candidatsRef(b);
    for (const x of sa) if (sb.has(x)) return true;
    return false;
  };

  const getFiliereId = (f) => String((f && (f.filiere_id !== undefined ? f.filiere_id : f.id || f._id)) || '');

  // meme logique que l'ecran Inscriptions : filieres de l'annee active
  const getFilieresAnnee = () => {
    if (!anneeActive) return filieres;
    const deLAnnee = filieres.filter(f => {
      const ids = [f.annee_id, f.annee_universitaire_id, f.annee].filter(v => v !== undefined && v !== null).map(refId);
      return ids.includes(anneeActive);
    });
    return deLAnnee.length > 0 ? deLAnnee : filieres;
  };

  const getNiveauxDeFiliere = (filiereId) => {
    if (!filiereId) return [];
    const ids = new Set();
    filiereNiveaux.forEach(l => { if (refId(l.filiere_id) === String(filiereId)) ids.add(String(l.niveau_id)); });
    const fil = filieres.find(f => getFiliereId(f) === String(filiereId));
    if (fil) (fil.niveaux || []).forEach(n => { if (n && n.niveau_id) ids.add(String(n.niveau_id)); });
    return niveaux
      .filter(n => ids.has(getNiveauId(n)))
      .slice()
      .sort((a, b) => String(a.intitule || '').localeCompare(String(b.intitule || '')));
  };

  // niveaux de l'annee active, tous filieres confondues (repli quand aucune filiere n'est choisie)
  const getNiveauxAnnee = () => {
    const ids = new Set();
    getFilieresAnnee().forEach(f => { getNiveauxDeFiliere(getFiliereId(f)).forEach(n => ids.add(getNiveauId(n))); });
    if (ids.size === 0) return niveaux;
    return niveaux.filter(n => ids.has(getNiveauId(n)))
      .slice().sort((a, b) => String(a.intitule || '').localeCompare(String(b.intitule || '')));
  };

  // Le niveau suffit a designer une filiere : on affiche donc directement tous les niveaux
  // de l'annee, avec la filiere en suffixe. Demander la filiere d'abord obligeait a faire
  // deux choix pour une information deja portee par le niveau.
  const getLibelleNiveau = (n) => {
    const intitule = String(n.intitule || n.nom || n.code || '');
    const fil = getFiliereDeNiveau(n);
    const nomFiliere = fil ? String(fil.intitule || fil.nom || fil.code_filiere || '') : '';
    if (!nomFiliere) return intitule;
    // on evite "CPI 1 — CPI 1" quand la filiere porte le meme nom que le niveau
    if (sansAccents(nomFiliere).toLowerCase() === sansAccents(intitule).toLowerCase()) return intitule;
    return intitule ? `${intitule} — ${nomFiliere}` : nomFiliere;
  };

  // La base stocke periodes.niveau_id sous forme de CODE texte ("8"), alors que l identifiant
  // du niveau est un ObjectId. On renvoie donc toutes les formes possibles pour pouvoir
  // comparer sans jamais perdre une periode par difference de type.
  const getNiveauId = (n) => String((n && (n.niveau_id !== undefined ? n.niveau_id : n.id || n._id)) || '');

  const getRefsNiveau = (n) => {
    if (!n) return [];
    const refs = new Set();
    if (n.niveau_id !== undefined && n.niveau_id !== null) refs.add(String(n.niveau_id));
    if (n.id !== undefined && n.id !== null) refs.add(String(n.id));
    if (n._id !== undefined && n._id !== null) refs.add(String(n._id));
    if (n.code !== undefined && n.code !== null) refs.add(String(n.code));
    refs.delete('');
    return [...refs];
  };

  const memeNiveau = (niveau, valeur) => getRefsNiveau(niveau).includes(String(valeur));

  // Une copie annuelle porte le MEME code que le niveau d origine : IRM2 existe en
  // niveau 2 (code "2") et en niveau 18 (code "2", copie 2027). Comme getRefsNiveau
  // inclut ce code, une comparaison par code ne distingue pas les deux annees et
  // ramene les periodes comme les matieres des deux. La periode du bon niveau etait
  // alors tiree au sort, donc la liste affichait les matieres de l annee precedente.
  // On compare donc sur l identifiant strict, seul lien non ambigu entre les documents.
  const memeNiveauStrict = (niveau, valeur) => {
    if (!niveau || valeur === undefined || valeur === null) return false;
    const v = String(valeur);
    if (typeof niveau !== 'object') return v === String(niveau);
    return v === getNiveauId(niveau) || v === String(niveau._id || '') || v === refId(niveau._id);
  };

  // Le strict d'abord, la comparaison large seulement en dernier recours : une base ou
  // les periodes ne stockent que des codes ne doit pas se retrouver sans aucune periode.
  const filtrerParNiveau = (liste, niveau, valeurDe) => {
    if (!niveau) return liste;
    // un niveau parfois designe par un simple texte : la comparaison stricte est deja
    // une egalite dans ce cas, le repli large n apporte rien
    if (typeof niveau !== 'object') return liste.filter(x => String(valeurDe(x)) === String(niveau));
    const strict = liste.filter(x => memeNiveauStrict(niveau, valeurDe(x)));
    if (strict.length > 0) return strict;
    return liste.filter(x => memeNiveau(niveau, valeurDe(x)));
  };

  // On recoit le niveau entier, pas seulement son id : c est la seule facon de comparer
    // correctement une periode dont niveau_id est un code texte.
    const getStatsNiveau = (niveau) => {
    const pers = filtrerParNiveau(periodes, niveau, p => p.niveau_id);
    const duNiveau = filtrerParNiveau(modules, niveau, m => m.niveau_id);
    const modIds = new Set(
      duNiveau
        .filter(m => pers.some(p => String(m.periode_id) === String(p._id)))
        .map(m => String(m._id || m.id))
    );
    const mats = modIds.size > 0 ? matieres.filter(m => modIds.has(String(m.module_id))) : [];
    return { periodes: pers.length, modules: modIds.size, matieres: mats.length };
  };

  const detecterNiveau = (classe) => {
    const nom = classeNom(classe);
    if (!nom || niveaux.length === 0) return null;
    const scores = niveaux
      .map(n => ({ n, s: scoreNiveau(n, nom) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s);
    if (scores.length === 0) return null;
    const avecData = scores.map(x => ({ ...x, ...getStatsNiveau(x.n) }));
    // Le score decide du niveau. Le volume de matieres ne sert qu a departager des niveaux
    // qui obtiennent le MEME score : sinon un niveau sans matieres se voyait remplacer par
    // un niveau moins pertinent mais bien dote, et la classe se retrouvait rattachée au
    // mauvais programme.
    const meilleur = avecData[0].s;
    const finalistes = avecData.filter(x => x.s === meilleur);
    // Deux niveaux portent le meme intitule pour deux annees differentes : IRM2 existe
    // en niveau 2 (annee 1) et en niveau 18 (annee 2). Une classe ne precise ni niveau
    // ni annee, et le volume de matieres ne departage plus rien des que les deux
    // copies portent le meme nombre de modules : le premier venu ressortait alors.
    // L annee active est la seule donnee fiable ici, on s y fie.
    const deLAnnee = anneeActive
      ? finalistes.filter(x => x.n.annee_id !== undefined && x.n.annee_id !== null
        && String(x.n.annee_id) === String(anneeActive))
      : [];
    // Une copie marquee de l annee active peut etre une coquille vide. L3 MASI EAF
    // declare niveau_id 48, seul niveau portant ses 14 modules et ses 15 matieres,
    // mais son champ annee_id est vide alors que la copie de meme nom, le niveau 23,
    // porte annee_id 2 sans aucun module. Retrecir d office a l annee active ecartait
    // donc le seul niveau qui avait des donnees, et la liste des matieres devenait vide.
    // On ne retrecit que si un niveau de l annee active porte effectivement des donnees.
    const avecDonnees = deLAnnee.some(x => x.matieres > 0 || x.modules > 0);
    const pool = avecDonnees ? deLAnnee : finalistes;
    const best = pool.find(x => x.matieres > 0) || pool.find(x => x.modules > 0) || pool[0];
    return best.n;
  };

  // Une classe designe son niveau par niveau_id, qui est une identite. Le CODE ne l est pas :
  // L3 Dev existe en niveau 3 (code "3") et en niveau 19 (meme code "3", copie 2026/2027).
  // Resoudre "3" par code ramene donc le niveau 19 et la classe se voit attribuer le
  // programme de l annee precedente. On ne compare que niveau_id, _id et id.
  const niveauParIdentifiant = (valeur) => {
    if (valeur === undefined || valeur === null || valeur === '') return null;
    const cible = String(valeur);
    return niveaux.find(n => String(n.niveau_id) === cible || String(n._id || '') === cible || String(n.id || '') === cible) || null;
  };

  // Un niveau peut etre une coquille : la copie annuelle reprend l intitule et le code
  // sans reprendre les modules. Le niveau 22 (« L3 DCMD », annee 2) est vide alors que
  // ses modules sont sur le niveau 34 ; le niveau 23 (« L3 EAF ») est vide alors que
  // ses modules sont sur le 48. Comme filiereNiveaux ne relie que la coquille, la
  // creation d une classe ne peut proposer que ce niveau : elle est condamnee a afficher
  // zero matiere. Si le niveau declare par la classe ne porte aucun module de l annee
  // active, on cherche donc parmi les niveaux de meme programme celui qui en porte.
  const niveauPorteDesModules = (n) => {
    if (!n || typeof n !== 'object') return false;
    const duNiveau = filtrerParNiveau(modules, n, m => m.niveau_id);
    if (duNiveau.length === 0) return false;
    if (!anneeActive) return true;
    return duNiveau.some(m => String(m.annee_universitaire_id) === String(anneeActive));
  };

  const modulesActifsDuNiveau = (n) => filtrerParNiveau(modules, n, m => m.niveau_id)
    .filter(m => !anneeActive || String(m.annee_universitaire_id) === String(anneeActive));

  const niveauxDuMemeProgramme = (n) => {
    if (!n || typeof n !== 'object') return [];
    const code = n.code === undefined || n.code === null ? '' : String(n.code).trim();
    const intitule = compact(n.intitule || n.nom || '');
    return niveaux.filter(x => {
      if (!x || x === n) return false;
      const c = x.code === undefined || x.code === null ? '' : String(x.code).trim();
      if (code && c === code) return true;
      return !!intitule && compact(x.intitule || x.nom || '') === intitule;
    });
  };

  // Niveau tel que la CLASSE le declare, sans passer par le filtre choisi a l ecran.
  // niveau_id est la source de verite. La reconnaissance par le nom (detecterNiveau)
  // ne vient qu en dernier recours, pour les classes qui ne declarer aucun niveau.
  const niveauDeClasse = (classe) => {
    if (!classe) return null;
    // L ordre compte : niveau_id d abord, puis un niveau stocke en objet. Ni l un ni l autre
    // ne passe par le code.
    let declare = niveauParIdentifiant(classe.niveau_id);
    if (!declare && classe.niveau) {
      declare = typeof classe.niveau === 'object'
        ? (niveauParIdentifiant(classe.niveau.niveau_id) || niveauParIdentifiant(classe.niveau._id))
        : niveauParIdentifiant(classe.niveau);
    }
    if (!declare) declare = detecterNiveau(classe);
    if (!declare || niveauPorteDesModules(declare)) return declare;
    const sauv = niveauxDuMemeProgramme(declare).filter(niveauPorteDesModules);
    if (sauv.length === 0) return declare;
    const parModules = sauv
      .map(n => ({ n, c: modulesActifsDuNiveau(n).length }))
      .sort((a, b) => b.c - a.c);
    return parModules[0].n;
  };

  const getNiveauCourant = (classe) => {
    if (filtreNiveauMatiere) {
      const choisi = niveaux.find(n => getNiveauId(n) === String(filtreNiveauMatiere));
      if (choisi) return choisi;
    }
    return niveauDeClasse(classe);
  };

  const getFiliereDeNiveau = (niveau) => {
    if (!niveau) return null;
    // La collection filiere-niveaux est vide dans cette base, et le champ filieres[].niveaux
    // contient des CODES de niveau ("1", "17"...) et non des _id. Comparer ces codes a un
    // ObjectId ne trouvait jamais rien : la filiere restait introuvable. On compare donc le
    // niveau a toutes ses references possibles, _id compris.
    const refs = getRefsNiveau(niveau);
    const lien = filiereNiveaux.find(fn => refs.includes(String(fn.niveau_id)) || refs.includes(String(refId(fn.niveau_id))));
    if (lien) {
      const f = filieres.find(x => String(x.filiere_id) === String(lien.filiere_id) || String(x._id) === String(lien.filiere_id));
      if (f) return f;
    }
    // plusieurs filieres peuvent pointer le meme code : on privilegie celle de l annee active
    const candidates = filieres.filter(f => (f.niveaux || []).some(n => refs.includes(String((n && (n.niveau_id || n)) || ''))));
    if (candidates.length === 0) return null;
    if (candidates.length === 1 || !anneeActive) return candidates[0];
    const deLAnnee = candidates.filter(f => {
      const ids = [f.annee_id, f.annee_universitaire_id, f.annee].filter(v => v !== undefined && v !== null).map(refId);
      return ids.length === 0 || ids.includes(anneeActive);
    });
    return deLAnnee[0] || candidates[0];
  };

  const getFiliereDeClasse = (classe) => {
    if (!classe) return null;
    const cible = classe.filiere || classe.filiere_id;
    if (cible) {
      const trouve = filieres.find(f => memeRef(f, cible) || String(f.filiere_id) === String(refId(cible)));
      if (trouve) return trouve;
    }
    return getFiliereDeNiveau(getNiveauCourant(classe));
  };

  const getNiveauIdsDeClasse = (classe) => {
    const ids = new Set();
    if (!classe) return ids;
    const niv = getNiveauCourant(classe);
    if (niv) ids.add(getNiveauId(niv));
    const fil = getFiliereDeClasse(classe);
    const fid = fil ? String(fil.filiere_id || fil._id) : '';
    if (fid) {
      filiereNiveaux.forEach(fn => {
        if (String(fn.filiere_id) === fid) ids.add(String(fn.niveau_id));
      });
      (fil.niveaux || []).forEach(n => { if (n && n.niveau_id) ids.add(String(n.niveau_id)); });
    }
    return ids;
  };

  // une meme periode (meme code semestre) peut exister pour plusieurs annees : on garde la plus utile
  const poidsPeriode = (p) => {
    const pid = String(p._id || p.id);
    const mods = modules.filter(m => String(m.periode_id) === pid);
    const modIds = new Set(mods.map(m => String(m._id || m.id)));
    const nbMat = modIds.size > 0 ? matieres.filter(m => modIds.has(String(m.module_id))).length : 0;
    const date = p.dateDebut || p.createdAt || null;
    return {
      matieres: nbMat,
      modules: mods.length,
      active: p.estActive === false ? 0 : 1,
      date: date ? new Date(date).getTime() || 0 : 0
    };
  };

  const periodeMieux = (a, b) => {
    const pa = poidsPeriode(a);
    const pb = poidsPeriode(b);
    if (pa.matieres !== pb.matieres) return pa.matieres > pb.matieres;
    if (pa.modules !== pb.modules) return pa.modules > pb.modules;
    if (pa.active !== pb.active) return pa.active > pb.active;
    return pa.date > pb.date;
  };

  const getPeriodesDeClasse = (classe) => {
    const niv = getNiveauCourant(classe);
    const base = niv
      ? filtrerParNiveau(periodes, niv, p => p.niveau_id)
      : periodes.filter(p => getNiveauIdsDeClasse(classe).has(String(p.niveau_id)));
    const parCode = new Map();
    base.forEach(p => {
      const code = codeSemestre(p);
      if (!code) return;
      const courant = parCode.get(code);
      if (!courant || periodeMieux(p, courant)) parCode.set(code, p);
    });
    return [...parCode.values()].sort((a, b) => String(codeSemestre(a)).localeCompare(String(codeSemestre(b))));
  };

  const codeSemestre = (p) => {
    if (!p) return '';
    const fromCode = String(p.code || '').toUpperCase().match(/^S(\d+)/);
    if (fromCode) return `S${fromCode[1]}`;
    const fromNom = String(p.nom || '').toUpperCase().match(/S(\d+)/);
    if (fromNom) return `S${fromNom[1]}`;
    return p.code ? String(p.code).toUpperCase() : '';
  };

  const getPeriodeFiltre = () => {
    if (!filtreMatiereActif || filtreSemestreMatiere === 'all') return null;
    return periodes.find(x => String(x._id) === String(filtreSemestreMatiere)) || null;
  };

  const getModulesDePeriode = (classe, periodeId) => {
    if (!periodeId) return [];
    const niv = getNiveauCourant(classe);
    const parPeriode = modules.filter(m => String(m.periode_id) === String(periodeId));
    if (!niv) return parPeriode;
    // meme piege que pour les periodes : le module porte un niveau_id dont la forme
    // peut differer de celle du niveau, donc on compare sur toutes les references possibles
    const duNiveau = filtrerParNiveau(parPeriode, niv, m => m.niveau_id);
    return duNiveau.length > 0 ? duNiveau : parPeriode;
  };

  const getModuleDeMatiere = (m) => modules.find(x => String(x._id) === String(m.module_id)) || null;

  const getCodeSemestreMatiere = (m) => {
    const pid = m.periode_id || m.periode;
    if (pid) {
      const p = periodes.find(x => String(x._id) === String(pid) || codeSemestre(x) === String(pid).toUpperCase());
      const c = codeSemestre(p);
      if (c) return c;
    }
    const mod = getModuleDeMatiere(m);
    if (mod) {
      const c = codeSemestre(periodes.find(x => String(x._id) === String(mod.periode_id)));
      if (c) return c;
    }
    return '';
  };

  // Une seance commune a plusieurs niveaux porte une matiere par classe : le champ
  // subjectsByClass de la lecon fait autorite. S il est absent, la seance conserve une
  // matiere unique, valable pour toutes les classes : c est le cas de toutes les seances
  // programmees avant cette evolution.
  // Chaque classe dispose d une ligne : une classe sans entree retombe sur subjectId, pour
  // qu aucune classe d une seance commune ne disparaisse de l affichage.
  const getMatieresDeLecon = (lecon) => {
    const ids = lecon.classIds || [];
    if (ids.length < 2) return [];
    const parClasse = (lecon.subjectsByClass && typeof lecon.subjectsByClass === 'object') ? lecon.subjectsByClass : {};

    const ligne = (cid) => {
      const mid = parClasse[String(cid)] || lecon.subjectId;
      if (!mid) return { classId: String(cid), matiereId: null, nom: '' };
      const m = matieres.find(x => String(x.id) === String(mid) || String(x._id) === String(mid));
      return { classId: String(cid), matiereId: mid, nom: m ? (m.name || m.nom || lecon.subjectName || '') : (lecon.subjectName || '') };
    };

    const lignes = ids.map(ligne);
    // seance ancienne : toutes les classes partagent la matiere unique, on affiche le titre
    const toutesIdentiques = lignes.every(l => String(l.matiereId) === String(lecon.subjectId));
    if (toutesIdentiques) return [];
    return lignes.filter(l => l.matiereId || l.nom);
  };

  // Matieres disponibles pour une classe donnee et un semestre donne, utilisees pour
  // alimenter une liste par classe quand la seance est commune a plusieurs niveaux.
  const getMatieresPourClasse = (classe, periodeId) => dedoublonnerMatieres(getMatieresDeClasse(classe, periodeId));

  // Classes cochees dans la modale, resolues en objets de classe
  const classesDuFormulaire = () => (newCours.classes || [])
    .map(cid => classes.find(c => classeId(c) === String(cid)) || classes.find(c => classeNom(c) === cid))
    .filter(Boolean);

  // Une seance est dite "a matieres par classe" des que les classes cochees n appartiennent
  // pas toutes au meme niveau : CPI1 et L1 IRM n ont pas les memes matieres, un seul
  // selecteur ne saurait pas choisir les deux.
  const classesOntNiveauxDifferents = () => {
    const cls = classesDuFormulaire();
    if (cls.length < 2) return false;
    const niveaux = cls.map(c => {
      const n = getNiveauCourant(c);
      return n ? String(getNiveauId(n) || n.code || n.intitule) : null;
    }).filter(Boolean);
    return new Set(niveaux).size > 1;
  };

  // Un seul lecon porte la seance commune ; subjectsByClass y rattache une matiere par classe.
  // subjectId reste renseigne avec la matiere de la classe de reference, ce qui garde les
  // anciennes lectures (et les seances sans matieres par classe) fonctionnelles.
  const calculerSubjectsByClass = () => {
    const cls = classesDuFormulaire();
    const parClasse = {};
    cls.forEach((c, i) => {
      const cid = classeId(c);
      const mid = (newCours.matieresParClasse || {})[cid] || (i === 0 ? newCours.matiereId : '');
      if (cid && mid) parClasse[cid] = mid;
    });
    const matiereDeReference = cls.length > 0
      ? (newCours.matieresParClasse || {})[classeId(cls[0])] || newCours.matiereId
      : newCours.matiereId;
    return { subjectsByClass: parClasse, matiereDeReference };
  };

  const deLAnneeActive = (doc) =>
    Boolean(anneeActive) && String((doc && doc.annee_universitaire_id) || '') === String(anneeActive);

  // Deux IRM2 coexistent : le niveau 2 pour 2025/2026 et le niveau 18 pour 2026/2027,
  // memes codes de modules, memes periodes en apparence. Toute resolution passant par le
  // niveau ou par la periode peut donc aboutir a l annee precedente. L annee active est
  // portee explicitement par les documents : on ne propose que les matieres de cette
  // annee, ce qui rend le resultat independant du niveau devine.
  const matieresDeLAnnee = () => {
    if (!anneeActive) return matieres;
    const deLAnnee = matieres.filter(m => deLAnneeActive(m));
    return deLAnnee.length > 0 ? deLAnnee : matieres;
  };

  // Chaine de resolution des matieres d un groupe. Elle est volontairement STRICTE :
  // aucune etape ne peut renvoyer plus large que ce qui a ete demande.
  //
  // Le defaut de la version precedente etait l inverse : a chaque etape, si le filtre
  // ne renvoyait rien, on elargissait a l etape suivante jusqu a retomber sur TOUTES les
  // matieres de l annee. Resultat : pour un groupe dont le niveau n a aucune matiere
  // (niveau 3 pour 2026/2027, les matieres etant passees au niveau 19), la liste
  // affichee n etait pas vide du tout, elle etait simplement celle d un AUTRE niveau.
  // C est ce qui faisait dire que la liste de l emploi ne correspondait pas au groupe.
  //
  // Regle : groupe -> niveau du groupe -> modules de ce niveau -> matieres de ces modules.
  // Le semestre ne fait que restreindre les modules. Si aucun module du niveau n existe
  // pour l annee active, on ne retourne RIEN et on le dit, plutot que d inventer une liste.
  const getMatieresDeClasse = (classe, periodeId) => {
    const niv = getNiveauCourant(classe);

    // Aucun niveau ni aucune classe : on ne peut rien rattacher, on ne devine pas.
    if (!niv) {
      const toutes = anneeActive ? matieres.filter(deLAnneeActive) : matieres;
      return toutes.length > 0 ? toutes : [];
    }

    // 1. Modules du niveau, restreints au semestre demande.
    //    On ne retombe JAMAIS sur les modules d un autre niveau : getModulesDePeriode
    //    renvoie [] si le niveau n en a aucun.
    const modulesDuNiveau = filtrerParNiveau(modules, niv, m => m.niveau_id)
      .filter(m => !anneeActive || String(m.annee_universitaire_id) === String(anneeActive));
    if (modulesDuNiveau.length === 0) return [];

    const modulesRetenus = periodeId
      ? modulesDuNiveau.filter(m => String(m.periode_id) === String(periodeId))
      : modulesDuNiveau;
    const ids = new Set(
      (modulesRetenus.length > 0 ? modulesRetenus : modulesDuNiveau).map(m => String(m._id || m.id))
    );

    // 2. Matieres portees par ces modules, et uniquement celles de l annee active.
    return matieres.filter(m =>
      ids.has(String(m.module_id)) && (!anneeActive || deLAnneeActive(m)));
  };

  // Compare le programme complet du niveau a ce qui est reellement propose. Permet
  // d afficher un avertissement quand l annee des matieres du niveau ne correspond pas
  // a l annee active, au lieu de servir silencieusement les matieres d une autre annee.
  // Comparaison STRICTE volontaire : les niveaux 3 et 19 sont deux copies du meme code
  // "3", la comparaison large les confondrait et remitrait les matieres des deux annees.
  const getDiagnosticNiveau = (classe) => {
    const niv = getNiveauCourant(classe);
    if (!niv) return null;
    const attendu = matieres.filter(m => memeNiveauStrict(niv, m.niveau_id));
    const deLAnnee = attendu.filter(deLAnneeActive);
    const autresAnnees = [...new Set(attendu
      .filter(m => !deLAnneeActive(m))
      .map(m => String(m.annee_universitaire_id)))].filter(Boolean);
    return { niveau: niv, total: attendu.length, deLAnnee: deLAnnee.length, autresAnnees };
  };

  // certaines matieres existent en double (copies d'anneesUniversitaires differentes)
  const dedoublonnerMatieres = (liste) => {
    const vus = new Set();
    return liste.filter(m => {
      const cle = String(m.code || m.name || m.id || m._id || '').trim().toLowerCase();
      if (!cle) return true;
      if (vus.has(cle)) return false;
      vus.add(cle);
      return true;
    });
  };

  const getGroupesMatieres = () => {
    const classe = classes.find(c => classeId(c) === String(filtreGroupeMatiere)) || classes.find(c => classeNom(c) === selectedClasse);
    const niveau = getNiveauCourant(classe);
    const periode = getPeriodeFiltre();
    const codeFiltre = codeSemestre(periode);
    let liste = getMatieresDeClasse(classe, periode ? periode._id : '');
    let semestreIgnore = false;
    if (codeFiltre) {
      const parSemestre = liste.filter(m => getCodeSemestreMatiere(m) === codeFiltre);
      if (parSemestre.length > 0) liste = parSemestre;
      else semestreIgnore = true;
    }
    liste = dedoublonnerMatieres(liste);
    const periodesDispo = getPeriodesDeClasse(classe);
    const codes = [...new Set(periodesDispo.map(codeSemestre).filter(Boolean))].sort();
    const groupes = codes
      .map(code => {
        const p = periodesDispo.find(x => codeSemestre(x) === code);
        return {
          code,
          label: p && p.nom ? `${code} — ${p.nom}` : code,
          matieres: liste.filter(m => getCodeSemestreMatiere(m) === code)
        };
      })
      .filter(g => g.matieres.length > 0);
    const sans = liste.filter(m => !codes.includes(getCodeSemestreMatiere(m)));
    if (sans.length > 0) groupes.push({ code: 'other', label: 'Autres matières', matieres: sans });
    if (groupes.length === 0) groupes.push({ code: 'other', label: 'Autres matières', matieres: liste });
    if (newCours.matiereId && !groupes.some(g => g.matieres.some(m => String(m.id) === String(newCours.matiereId)))) {
      const current = matieres.find(x => String(x.id) === String(newCours.matiereId));
      if (current) groupes[groupes.length - 1].matieres = [current, ...groupes[groupes.length - 1].matieres];
    }
    const filiere = getFiliereDeNiveau(niveau) || getFiliereDeClasse(classe);
    const stats = niveau ? getStatsNiveau(niveau) : { periodes: 0, modules: 0, matieres: 0 };
    const diagnostic = getDiagnosticNiveau(classe);
    // Niveau ecrit dans la classe, qui peut différer de celui retenu : filiereNiveaux ne
    // relie parfois qu une coquille vide, et niveauDeClasse a alors repris le programme
    // d un niveau voisin. On l affiche pour que l ecart ne reste pas silencieux.
    const niveauDeclare = classe ? niveauParIdentifiant(classe.niveau_id) : null;
    return { groupes, total: liste.length, semestreIgnore, filiere, niveau, niveauDeclare, stats, diagnostic };
  };

  // Recherche une seance strictement identique a celle que l on veut creer : meme jour, meme
  // creneau, meme duree, meme professeur, meme salle. C est le cas quand la direction
  // regroupe deux classes qui avaient chacune leur propre seance : plutot que de refuser,
  // on rattache les classes a la seance existante.
  const trouverSeanceIdentique = (jour, periode, duree, professeurId, salleId, regimeCours) => {
    if (!salleId && !professeurId) return null;
    const jourBit = getDayBit(jour);
    for (const card of cards) {
      const lecon = lecons.find(l => l.id === card.lessonId);
      if (!lecon) continue;
      if (normaliserRegime(card.regime || lecon.regime) !== normaliserRegime(regimeCours)) continue;
      if (!((parseInt(card.days) || 0) & jourBit)) continue;
      if (parseInt(card.period) !== periode) continue;
      if ((parseInt(card.duree) || 1) !== (duree || 1)) continue;

      const salleCard = String(card.classroomId || lecon.classroomId || '');
      const profCard = String(lecon.teacherId || '');
      if (salleId && salleCard !== String(salleId)) continue;
      if (professeurId && profCard !== String(professeurId)) continue;

      return { card, lecon };
    }
    return null;
  };

  // Ajoute des classes a une seance existante sans toucher au reste : c est ainsi qu un
  // cours commun se construit quand la direction part de deux seances deja programmees.
  const rattacherClasses = async (seance, classesChoisies, token, options = {}) => {
    const headers = { Authorization: `Bearer ${token}` };
    const existantes = (seance.lecon.classIds || []).map(refId);
    const ajoutees = classesChoisies.filter(id => !existantes.includes(refId(id)));

    // Regroupement : subjectId et subjectName ne sont JAMAIS modifies. Ce sont les
    // champs de la seance commune, ils decrivent la matiere de reference. Ecraser
    // subjectId avec la matiere de la classe qui rejoint le groupe detruisait la matiere
    // des classes deja regroupees. Chaque matiere vit dans subjectsByClass.
    const parClasse = { ...(seance.lecon.subjectsByClass || {}) };
    // Les classes deja regroupees sans entree conservent explicitement la matiere
    // qu elles affichaient, pour qu aucune ne soit ensuite consideree sans matiere.
    existantes.forEach(cid => {
      if (!parClasse[cid] && seance.lecon.subjectId) parClasse[cid] = seance.lecon.subjectId;
    });
    Object.entries(options.aCompleter || {}).forEach(([cid, mid]) => {
      if (mid) parClasse[cid] = mid;
    });

    if (ajoutees.length === 0 && Object.keys(options.aCompleter || {}).length === 0) {
      return { ajoutees: 0 };
    }

    await axios.put('/api/emploi/lecons', {
      id: seance.lecon.id,
      subjectId: seance.lecon.subjectId,
      subjectName: seance.lecon.subjectName,
      teacherId: seance.lecon.teacherId || null,
      classroomId: seance.lecon.classroomId || null,
      classIds: [...existantes, ...ajoutees],
      subjectsByClass: parClasse
    }, { headers });
    return { ajoutees: ajoutees.length };
  };

  const getCours = (jour, periode) => {
    return emploiData.find(c => c.jour === jour && c.periode === periode);
  };

  const getStarts = (jour, periode) => {
    return emploiData.filter(c => c.jour === jour && c.periode === periode);
  };

  const presencesConflit = (jour, periode, duree, professeurId, salleId, excludeCardId, classesCibles, regimeCible = regime) => {
    const conflits = { prof: [], salle: [], classe: [] };
    if (!creneauValide(regimeCible, jour, periode, duree || 1)) return conflits;
    if (!professeurId && !salleId && !classesCibles) return conflits;
    const jourBit = getDayBit(jour);
    const prises = [];
    for (let i = periode; i < periode + (duree || 1); i++) prises.push(i);
    // ignore la carte en cours d edition : sans cela, deplacer ou regrouper un cours le
    // comparerait a lui-meme et Announces un faux conflit de prof et de salle
    const enEdition = excludeCardId
      ? new Set([String(excludeCardId), String(excludeCardId)])
      : null;
    for (const card of cards) {
      if (enEdition && enEdition.has(String(card._id))) continue;
      const lecon = lecons.find(l => l.id === card.lessonId);
      if (!lecon) continue;
      if (normaliserRegime(card.regime || lecon.regime) !== normaliserRegime(regimeCible)) continue;
      if ((parseInt(card.days) || 0) & jourBit) {
        const cPeriode = parseInt(card.period);
        const cDuree = parseInt(card.duree) || 1;
        const chevauche = prises.some(p => p >= cPeriode && p < cPeriode + cDuree);
        if (chevauche) {
          const nom = lecon.subjectName || lecon.subjectId || 'cours';
          if (professeurId && lecon.teacherId && String(lecon.teacherId) === String(professeurId)) conflits.prof.push(nom);
          if (salleId && (card.classroomId || lecon.classroomId) && String(card.classroomId || lecon.classroomId) === String(salleId)) conflits.salle.push(nom);
          // une classe ne peut pas suivre deux cours au meme moment : on verifie chacune des
          // classes du regroupement, sinon on accepterait CPI1 + IRM dont IRM est deja occupe
          if (classesCibles && classesCibles.length) {
            const occupees = (lecon.classIds || []).map(refId);
            classesCibles.forEach(id => {
              if (occupees.includes(refId(id))) {
                const enCause = classes.find(x => classeId(x) === refId(id));
                conflits.classe.push(`${enCause ? classeNom(enCause) : 'une classe'} (${nom})`);
              }
            });
          }
        }
      }
    }
    return conflits;
  };

  const getSpanContinuing = (jour, periode) => {
    return emploiData.find(c => c.jour === jour && c.periode === periode - 1 && c.duree === 2);
  };

  const getTypeCours = (matiere) => {
    if (!matiere) return { bg: '#f3f4f6', color: '#6b7280', label: 'Cours', short: 'CM' };
    const nom = matiere.toLowerCase();
    if (nom.includes('tp') || nom.includes('pratique')) return { bg: '#fef3c7', color: '#92400e', label: 'Travaux Pratiques (TP)', short: 'TP' };
    if (nom.includes('td') || nom.includes('dirigé')) return { bg: '#dcfce7', color: '#166534', label: 'Travaux Dirigés (TD)', short: 'TD' };
    return { bg: '#dbeafe', color: '#1e40af', label: 'Cours Magistral (CM)', short: 'CM' };
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const classeName = selectedClasse;
    
    let tableRows = '';
    // une seule matiere a l impression : celle de la classe imprimee
    const idImprimee = classeId(classes.find(c => classeNom(c) === classeName));
    for (let i = 0; i < jours.length; i++) {
      const jour = jours[i];
      let row = `<tr>
        <td class="jour-col">${joursLabels[i]}</td>`;
      for (const horaire of horaires) {
        if (getSpanContinuing(jour, horaire.index)) continue;
        const cours = getCours(jour, horaire.index);
        if (cours) {
          const typeStyle = getTypeCours(cours.matiere);
          const colspan = cours.duree === 2 ? ' colspan="2"' : '';
          const ligneImprimee = cours.matieresParClasse
            ? cours.matieresParClasse.find(x => String(x.classId) === String(idImprimee))
            : null;
          const matiereImprimee = ligneImprimee ? (ligneImprimee.nom || 'Sans matière') : cours.matiere;
          row += `
            <td class="cours-cell"${colspan} style="background: ${cours.duree === 2 ? typeStyle.bg : 'transparent'};">
              <div class="cours-title" style="color: ${typeStyle.color};">${matiereImprimee}</div>
              <div class="cours-info">${cours.salle}</div>
              <div class="cours-info">${cours.professeur}</div>
            </td>
          `;
        } else {
          row += `<td class="cours-cell"></td>`;
        }
      }
      row += `</tr>`;
      tableRows += row;
    }
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Emploi du temps - ${classeName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          /* Paysage : la grille tient sur une seule page, toutes les cellules a la meme taille */
          @page { size: A4 landscape; margin: 8mm; }
          html, body { height: 100%; }
          body { font-family: 'Segoe UI', Arial, sans-serif; color: #333; display: flex; flex-direction: column; padding: 0; }
          .header { display: flex; align-items: center; gap: 14px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 2px solid #3b82f6; }
          .header .logo { width: 46px; height: 46px; object-fit: contain; flex: 0 0 auto; }
          .header-titres { flex: 1; text-align: center; }
          .header h1 { color: #1f2937; font-size: 17px; margin-bottom: 2px; }
          .header h2 { color: #3b82f6; font-size: 13px; margin-bottom: 0; }
          .classe-info { text-align: center; font-size: 22px; font-weight: 700; color: #1f2937; margin-bottom: 8px; }
          .grille { flex: 1 1 auto; min-height: 0; }
          table { width: 100%; height: 100%; table-layout: fixed; border-collapse: collapse; }
          col.jour { width: 8%; }
          col.slot { width: ${(92 / horaires.length).toFixed(2)}%; }
          thead tr { height: 10%; }
          tbody tr { height: ${(90 / jours.length).toFixed(2)}%; }
          th { background: #3b82f6; color: white; padding: 4px; text-align: center; font-weight: 600; font-size: 12px; border: 1px solid #94a3b8; }
          td { border: 1px solid #94a3b8; padding: 4px; text-align: center; vertical-align: middle; overflow-wrap: anywhere; }
          .heure-col { background: #f3f4f6; font-weight: bold; text-align: center; }
          .jour-col { background: #f3f4f6; font-weight: 700; font-size: 13px; text-align: center; text-transform: capitalize; }
          .cours-title { font-weight: 700; font-size: 12px; margin-bottom: 3px; }
          .cours-info { font-size: 10px; color: #6b7280; line-height: 1.3; }
          .footer { flex: 0 0 auto; display: flex; justify-content: space-between; font-size: 9px; color: #9ca3af; margin-top: 6px; padding-top: 5px; border-top: 1px solid #e5e7eb; }
          @media print {
            body { padding: 0; }
            th { background: #3b82f6 !important; color: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            td, .jour-col { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="/images/logo_sup2i.png" class="logo" alt="SUP2I" onerror="this.style.display='none'" />
          <div class="header-titres">
            <h1>SUP2I - Emploi du Temps</h1>
            <h2>Année Universitaire 2026/2027${estTA ? ' - Temps aménagé' : ''}</h2>
          </div>
        </div>
        <div class="classe-info">${classeName}</div>
        <div class="grille">
        <table>
          <colgroup><col class="jour"/>${horaires.map(() => '<col class="slot"/>').join('')}</colgroup>
          <thead>
            <tr>
              <th>Jour</th>
              ${horaires.map((h, i) => `<th>${estTA ? 'Créneau' : 'Séance'} ${i + 1}<br/>${h.label}</th>`).join('')}
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
        </div>
        <div class="footer">
          <span>Horaire généré : ${new Date().toLocaleDateString('fr-FR')}</span>
          <span>${classeName}</span>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    // le titre sert de nom de fichier quand on choisit « Enregistrer au format PDF »
    printWindow.document.title = `Emploi_du_temps_${String(classeName || '').replace(/\s+/g, '_')}`;
    printWindow.print();
  };

  const handleExportPDF = () => {
    handlePrint();
  };

  const handleAddCours = async () => {
    const classesChoisies = normaliserClasses(newCours.classes, classes, selectedClasse);
    if (classesChoisies.length === 0) {
      setMessage('⚠️ Choisissez au moins une classe');
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    // Un cours commun ne peut pas melanger une classe normale et une classe TA : leurs
    // creneaux ne sont pas les memes.
    const regimesChoisis = [...new Set(classesChoisies.map(cid => {
      const c = classes.find(x => classeId(x) === String(cid));
      return regimeDeClasse(c);
    }))];
    if (regimesChoisis.length !== 1) {
      setMessage('⚠️ Un cours commun ne peut pas melanger des classes normales et TA');
      setTimeout(() => setMessage(''), 6000);
      return;
    }
    const regimeCours = regimesChoisis[0] || regime;
    if (!creneauValide(regimeCours, newCours.jour, newCours.periode, newCours.duree || 1)) {
      setMessage(regimeCours === 'TA'
        ? '⚠️ Créneau TA invalide : soirée en semaine (19:00-22:00) ou week-end (09:30-16:30)'
        : '⚠️ Créneau invalide');
      setTimeout(() => setMessage(''), 6000);
      return;
    }

    // La matiere est validee plus bas : si une seance identique existe deja, on regroupe
    // et la matiere de la nouvelle classe reste a saisir dans le formulaire.

    // Si une seance strictement identique existe deja (meme jour, creneau, duree, prof, salle),
    // on ne bloque pas : on rattache les classes a cette seance. C est le regroupement que
    // la direction veut faire quand deux classes avaient chacune leur propre ligne.
    const seanceExistante = trouverSeanceIdentique(
      newCours.jour, newCours.periode, newCours.duree || 1, newCours.professeurId, newCours.salleId, regimeCours
    );
    // Regroupement : la seance deja programmee garde sa matiere, on ne rattache que les
    // classes. La matiere propre a la classe qui rejoint le groupe reste a saisir.
    if (seanceExistante) {
      const { subjectsByClass: aCompleter } = calculerSubjectsByClass();
      // seanceExistante vaut { card, lecon } : les classes sont sur la lecon
      const dejaRattachees = new Set((seanceExistante.lecon.classIds || []).map(refId));
      const nouvelles = classesChoisies.filter(cid => !dejaRattachees.has(refId(cid)));

      // Si aucune matiere par classe n a ete saisie mais que la liste unique l a ete, on la
      // applique aux classes qui rejoignent le groupe : c est le comportement d avant.
      const aRajouter = { ...aCompleter };
      if (Object.keys(aRajouter).length === 0 && newCours.matiereId) {
        nouvelles.forEach(cid => { aRajouter[cid] = newCours.matiereId; });
      }

      const manquantes = nouvelles.filter(cid => !aRajouter[refId(cid)]);
      if (manquantes.length > 0) {
        const noms = manquantes.map(cid => {
          const c = classes.find(x => classeId(x) === refId(cid));
          return (c ? classeNom(c) : cid) + ' sans matière';
        }).join(', ');
        setMessage(`Choisissez une matière pour : ${noms}`);
        setTimeout(() => setMessage(''), 8000);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const { ajoutees } = await rattacherClasses(seanceExistante, nouvelles, token, { aCompleter: aRajouter });
        await loadAllData();
        setShowAddModal(false);
        setNewCours({ jour: 'lundi', periode: 0, matiereId: '', matieresParClasse: {}, professeurId: '', salleId: '', duree: 1, classes: [] });
        setMessage(ajoutees > 0
          ? `✅ Regroupement effectué : ${ajoutees} classe(s) ajoutée(s) à la séance existante`
          : '✅ Ces classes suivent déjà cette séance');
        setTimeout(() => setMessage(''), 5000);
      } catch (err) {
        console.error('Erreur regroupement:', err);
        setMessage('Erreur lors du regroupement des classes');
        setTimeout(() => setMessage(''), 3000);
      }
      return;
    }

    const conflits = presencesConflit(newCours.jour, newCours.periode, newCours.duree || 1, newCours.professeurId, newCours.salleId, null, classesChoisies, regimeCours);
    if (conflits.prof.length > 0) {
      setMessage(`⚠️ Prof non disponible à ce créneau (${conflits.prof[0]})`);
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    if (conflits.salle.length > 0) {
      setMessage(`⚠️ Salle occupée à ce créneau (${conflits.salle[0]})`);
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    if (conflits.classe.length > 0) {
      setMessage(`⚠️ Classe déjà en cours à ce créneau : ${conflits.classe[0]}`);
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    // Chaque classe de la seance doit avoir une matiere. Quand les classes n ont pas le
    // meme niveau, une seule matiere ne convient pas a toutes.
    const { subjectsByClass, matiereDeReference } = calculerSubjectsByClass();
    const sansMatiere = classesChoisies.filter(cid => !subjectsByClass[String(cid)]);
    if (classesChoisies.length > 0 && sansMatiere.length > 0) {
      const noms = sansMatiere.map(cid => {
        const c = classes.find(x => classeId(x) === String(cid));
        return classeNom(c) || cid;
      });
      setMessage(`Sélectionnez une matière pour : ${noms.join(', ')}`);
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    try {
      const classe = classes.find(c => c.name === selectedClasse);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const newLessonId = `L${Date.now()}`;
      const newLesson = {
        id: newLessonId,
        subjectId: matiereDeReference || null,
        teacherId: newCours.professeurId || null,
        classroomId: newCours.salleId || null,
        classIds: classesChoisies,
        periodsPerWeek: 1,
        subjectName: matieres.find(m => String(m.id) === String(matiereDeReference))?.name,
        subjectsByClass,
        teacherName: professeurs.find(p => p.id === newCours.professeurId)?.name,
        classroomName: salles.find(s => s.id === newCours.salleId)?.name,
        regime: regimeCours
      };
      
      await axios.post('/api/emploi/lecons', newLesson, { headers });
      
      const daysBit = getDayBit(newCours.jour);
      const newCard = {
        lessonId: newLessonId,
        period: newCours.periode.toString(),
        days: daysBit.toString(),
        weeks: 1,
        classroomId: newCours.salleId || null,
        duree: newCours.duree || 1,
        regime: regimeCours
      };
      
      await axios.post('/api/emploi/cards', newCard, { headers });
      
      await loadAllData();
      setShowAddModal(false);
      setNewCours({ jour: 'lundi', periode: 0, matiereId: '', matieresParClasse: {}, professeurId: '', salleId: '', duree: 1, classes: [] });
      setMessage(classesChoisies.length > 1
        ? `Cours commun à ${classesChoisies.length} classes ajouté !`
        : 'Cours ajouté avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Erreur:', err);
      setMessage('Erreur lors de l\'ajout');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleEditCours = (cours) => {
    setEditingCours(cours);
    setNewCours({
      jour: cours.jour,
      periode: cours.periode,
      matiereId: cours.matiereId || '',
      // rechargement de la matiere propre a chaque classe quand la seance en porte une
      matieresParClasse: (cours.matieresParClasse || []).reduce((acc, x) => {
        acc[x.classId] = x.matiereId || '';
        return acc;
      }, {}),
      professeurId: cours.professeurId || '',
      salleId: cours.salleId || '',
      duree: cours.duree || 1,
      // un cours commun garde toutes ses classes cochées, sinon on perdrait le regroupement
      classes: (cours.classIds && cours.classIds.length)
        ? cours.classIds.map(String)
        : normaliserClasses([], classes, selectedClasse)
    });
    setShowAddModal(true);
  };

  const handleUpdateCours = async () => {
    const classesChoisies = normaliserClasses(newCours.classes, classes, selectedClasse);
    if (classesChoisies.length === 0) {
      setMessage('⚠️ Choisissez au moins une classe');
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    const regimesChoisis = [...new Set(classesChoisies.map(cid => {
      const c = classes.find(x => classeId(x) === String(cid));
      return regimeDeClasse(c);
    }))];
    if (regimesChoisis.length !== 1) {
      setMessage('⚠️ Un cours commun ne peut pas melanger des classes normales et TA');
      setTimeout(() => setMessage(''), 6000);
      return;
    }
    const regimeCours = regimesChoisis[0] || regime;
    if (!creneauValide(regimeCours, newCours.jour, newCours.periode, newCours.duree || 1)) {
      setMessage(regimeCours === 'TA'
        ? '⚠️ Créneau TA invalide : soirée en semaine (19:00-22:00) ou week-end (09:30-16:30)'
        : '⚠️ Créneau invalide');
      setTimeout(() => setMessage(''), 6000);
      return;
    }

    const conflits = presencesConflit(newCours.jour, newCours.periode, newCours.duree || 1, newCours.professeurId, newCours.salleId, editingCours.cardId, classesChoisies, regimeCours);
    if (conflits.prof.length > 0) {
      setMessage(`⚠️ Prof non disponible à ce créneau (${conflits.prof[0]})`);
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    if (conflits.salle.length > 0) {
      setMessage(`⚠️ Salle occupée à ce créneau (${conflits.salle[0]})`);
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    if (conflits.classe.length > 0) {
      setMessage(`⚠️ Classe déjà en cours à ce créneau : ${conflits.classe[0]}`);
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const { subjectsByClass, matiereDeReference } = calculerSubjectsByClass();
      const partagee = classesChoisies.length > 1;

      // Seance partagee : subjectId decrit la matiere de reference et ne bouge pas.
      // Chaque groupe garde la sienne dans subjectsByClass. Ecrire la matiere choisie dans
      // le formulaire au lieu de subjectsByClass supprimerait celle des autres groupes.
      const corps = {
        id: editingCours.leconId,
        subjectId: partagee
          ? (editingCours.matiereId || matiereDeReference || null)
          : (matiereDeReference || null),
        teacherId: newCours.professeurId || null,
        classroomId: newCours.salleId || null,
        classIds: classesChoisies,
        regime: regimeCours
      };
      if (partagee) {
        corps.subjectsByClass = subjectsByClass;
      } else {
        const nom = matieres.find(m => String(m.id) === String(matiereDeReference));
        corps.subjectName = nom ? (nom.name || nom.nom) : undefined;
      }

      await axios.put('/api/emploi/lecons', corps, { headers });
      
      const daysBit = getDayBit(newCours.jour);
      await axios.put('/api/emploi/cards', {
        id: editingCours.cardId,
        period: newCours.periode.toString(),
        days: daysBit.toString(),
        classroomId: newCours.salleId || null,
        duree: newCours.duree || 1,
        regime: regimeCours
      }, { headers });
      
      await loadAllData();
      setShowAddModal(false);
      setEditingCours(null);
      setMessage(classesChoisies.length > 1
        ? `Cours commun à ${classesChoisies.length} classes modifié !`
        : 'Cours modifié avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Erreur:', err);
      setMessage('Erreur lors de la modification');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleDeleteCours = async (cours) => {
    if (!window.confirm('Supprimer définitivement ce cours ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete('/api/emploi/cards', {
        data: { id: cours.cardId },
        headers
      });
      await axios.delete('/api/emploi/lecons', {
        data: { id: cours.leconId },
        headers
      });
      
      await loadAllData();
      setMessage('Cours supprimé avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Erreur:', err);
      setMessage('Erreur lors de la suppression');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const getDayBit = (jour) => {
    const days = { 'lundi': 1, 'mardi': 2, 'mercredi': 4, 'jeudi': 8, 'vendredi': 16, 'samedi': 32, 'dimanche': 64 };
    return days[jour] || 0;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div className="loading-spinner"></div>
        <p>Chargement de l'emploi du temps...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p style={{ color: '#ef4444' }}>{error}</p>
        <button onClick={loadAllData} style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 25%, #f3e8ff 100%)',
      padding: '1.5rem'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* En-tête */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>📅 Emploi du Temps</h1>
            <p style={{ color: '#6b7280' }}>Année Universitaire 2026/2027</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={handlePrint} style={{ padding: '0.75rem 1.5rem', background: '#10b981', border: 'none', borderRadius: '12px', color: 'white', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Printer size={18} /> Imprimer
            </button>
            <button onClick={handleExportPDF} style={{ padding: '0.75rem 1.5rem', background: '#3b82f6', border: 'none', borderRadius: '12px', color: 'white', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Download size={18} /> Exporter PDF
            </button>
            {peutEditerEmploi && (
              <>
                <button onClick={() => setEditMode(!editMode)} style={{ padding: '0.75rem 1.5rem', background: editMode ? '#10b981' : '#f59e0b', border: 'none', borderRadius: '12px', color: 'white', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Edit2 size={18} /> {editMode ? 'Mode consultation' : 'Mode édition'}
                </button>
                <button onClick={() => { setEditingCours(null); setNewCours({ jour: 'lundi', periode: 0, matiereId: '', matieresParClasse: {}, professeurId: '', salleId: '', duree: 1, classes: normaliserClasses([], classes, selectedClasse) }); setShowAddModal(true); }} style={{ padding: '0.75rem 1.5rem', background: '#8b5cf6', border: 'none', borderRadius: '12px', color: 'white', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Plus size={18} /> Ajouter un cours
                </button>
              </>
            )}
          </div>
        </div>

        {/* Message */}
        {message && (() => {
          const isSuccess = message.includes('succès');
          const isError = message.startsWith('⚠️') || message.includes('Erreur');
          return (
            <div style={{ background: isSuccess ? '#dcfce7' : isError ? '#fee2e2' : '#fef3c7', color: isSuccess ? '#166534' : isError ? '#b91c1c' : '#92400e', padding: '1rem', borderRadius: '12px', marginBottom: '1.5rem', textAlign: 'center' }}>
              {message}
            </div>
          );
        })()}

        {/* Sélecteur de classe */}
        {!isEtudiant && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '1rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>🎓 Classe</label>
          <select value={selectedClasse} onChange={(e) => setSelectedClasse(e.target.value)} style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            {classes.map(c => <option key={c.id} value={c.name}>{c.name}{regimeDeClasse(c) === 'TA' ? ' · TA' : ''}</option>)}
          </select>
          {estTA && (
            <p style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '0.5rem' }}>
              Temps aménagé : soirée en semaine (19:00-22:00) et week-end (09:30-16:30).
            </p>
          )}
        </div>
        )}
        {isEtudiant && selectedClasse && (
          <div style={{ background: 'white', borderRadius: '16px', padding: '1rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>🎓 Mon groupe</label>
            <div style={{ padding: '0.625rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', fontWeight: 600 }}>
              {selectedClasse}
            </div>
          </div>
        )}

        {/* Légende */}
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.5rem', padding: '0.75rem 1rem', background: 'white', borderRadius: '12px', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '16px', height: '16px', background: '#dbeafe', borderRadius: '4px' }}></div><span>Cours Magistral (CM)</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '16px', height: '16px', background: '#dcfce7', borderRadius: '4px' }}></div><span>Travaux Dirigés (TD)</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '16px', height: '16px', background: '#fef3c7', borderRadius: '4px' }}></div><span>Travaux Pratiques (TP)</span></div>
        </div>

        {/* Tableau Emploi du Temps */}
        <div style={{ background: 'white', borderRadius: '20px', overflowX: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.07)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '1rem', textAlign: 'center', width: '140px' }}>Jour</th>
                {horaires.map(h => <th key={h.index} style={{ padding: '1rem', textAlign: 'center', minWidth: '220px' }}>{h.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {jours.map(jour => (
                <tr key={jour} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', background: '#f9fafb', borderRight: '1px solid #e5e7eb', textTransform: 'capitalize' }}>{jour}</td>
                  {horaires.map(horaire => {
                    if (getSpanContinuing(jour, horaire.index)) return null;
                    const cours = getCours(jour, horaire.index);
                    const typeStyle = getTypeCours(cours?.matiere);
                    const isFused = cours && cours.duree === 2;
                    // Seance commune : chaque emploi n affiche que la matiere de sa classe.
                    // Les autres groupes restent accessibles au survol et via la ligne 👥.
                    const idMaClasse = classeId(classes.find(c => classeNom(c) === selectedClasse));
                    const ligneMoi = cours && cours.matieresParClasse
                      ? cours.matieresParClasse.find(x => String(x.classId) === String(idMaClasse))
                      : null;
                    const matiereAffichee = ligneMoi ? (ligneMoi.nom || 'Sans matière') : (cours ? cours.matiere : '');
                    const detailPartage = cours && cours.matieresParClasse && cours.matieresParClasse.length > 1
                      ? cours.matieresParClasse.map(x => x.classe + ' : ' + (x.nom || 'Sans matière')).join('\n')
                      : '';
                    return (
                      <td key={horaire.index} colSpan={isFused ? 2 : undefined} style={{ padding: '0.5rem 0.75rem', verticalAlign: 'top', background: isFused ? typeStyle.bg : 'transparent' }}>
                        {cours ? (
                          <div title={detailPartage || undefined} style={{ minHeight: '72px', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '0.25rem', padding: isFused ? '0.9rem 1rem' : '0.9rem', background: isFused ? 'transparent' : typeStyle.bg, borderRadius: isFused ? 0 : '10px', position: 'relative' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', background: typeStyle.color, color: '#fff', fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 8px', borderRadius: '99px' }}>{typeStyle.short}</div>
                            <div style={{ fontSize: isFused ? '1.05rem' : '0.95rem', fontWeight: '700', color: typeStyle.color, lineHeight: '1.25', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{matiereAffichee}</div>
                            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.1rem' }}>📍 {cours.salle} · 👨‍🏫 {cours.professeur}</div>
                            {cours.classesLiees && cours.classesLiees.length > 1 && (
                              <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#7c3aed', marginTop: '0.15rem', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                👥 {cours.classesLiees.join(' + ')}
                              </div>
                            )}
                            {cours.duree === 2 && (
                              <div style={{ fontSize: '0.65rem', fontWeight: '600', color: typeStyle.color, marginTop: '0.1rem' }}>⏱ Séances fusionnées — 3h</div>
                            )}
                            {editMode && peutEditerEmploi && (
                              <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.4rem', justifyContent: 'center' }}>
                                <button onClick={() => handleEditCours(cours)} style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.85)', border: `1px solid ${typeStyle.color}`, borderRadius: '6px', color: typeStyle.color, cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' }}><Edit2 size={10} /> Modifier</button>
                                <button onClick={() => handleDeleteCours(cours)} style={{ padding: '4px 8px', background: '#ef4444', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' }}><Trash2 size={10} /> Supprimer</button>
                              </div>
                            )}
                          </div>
                        ) : (editMode && peutEditerEmploi && creneauValide(regime, jour, horaire.index, 1) ? (
                          <button onClick={() => { setEditingCours(null); setNewCours({ jour: jour, periode: horaire.index, matiereId: '', matieresParClasse: {}, professeurId: '', salleId: '', duree: 1, classes: normaliserClasses([], classes, selectedClasse) }); setShowAddModal(true); }} style={{ width: '100%', padding: '0.75rem', background: '#f3f4f6', border: '2px dashed #cbd5e1', borderRadius: '10px', color: '#6b7280', cursor: 'pointer', fontSize: '0.875rem' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#e0e7ff'; e.currentTarget.style.borderColor = '#3b82f6'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#cbd5e1'; }}>+ Cliquer pour ajouter un cours</button>
                        ) : (
                          <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '10px', textAlign: 'center', color: '#9ca3af', fontSize: '0.875rem' }}>-</div>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal Ajout/Modification Cours */}
        {showAddModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', borderRadius: '20px', width: '90%', maxWidth: '500px', maxHeight: '80vh', overflow: 'auto', padding: '25px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2>{editingCours ? 'Modifier le cours' : 'Ajouter un cours'}</h2>
                <button onClick={() => { setShowAddModal(false); setEditingCours(null); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>✕</button>
              </div>
              
              <div style={{ marginBottom: '15px' }}>
          <label>Classes * {newCours.classes.length > 1 && <span style={{ color: '#7c3aed', fontWeight: 600 }}>· cours commun à {newCours.classes.length} classes</span>}</label>
          <div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '6px', maxHeight: '150px', overflowY: 'auto', background: '#fff' }}>
            {classes.map(c => {
              const id = classeId(c);
              const coche = newCours.classes.some(x => String(x) === id);
              return (
                <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', cursor: 'pointer', borderRadius: '6px' }}>
                  <input
                    type="checkbox"
                    checked={coche}
                    onChange={() => {
                      const suivantes = coche
                        ? newCours.classes.filter(x => String(x) !== id)
                        : [...newCours.classes, id];
                      // decocher une classe oublie sa matiere : elle n existe plus pour la seance
                      const parClasse = { ...(newCours.matieresParClasse || {}) };
                      if (coche) delete parClasse[id];
                      setNewCours({ ...newCours, classes: suivantes, matieresParClasse: parClasse });
                    }}
                  />
                  <span style={{ fontSize: '0.875rem', color: '#1f2937' }}>{classeNom(c)}</span>
                </label>
              );
            })}
          </div>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '6px' }}>
            Cochez plusieurs cases pour regrouper les classes sur une même séance. La classe affichée en haut reste la référence.
          </p>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label>Jour *</label>
          <select
            value={newCours.jour}
            onChange={(e) => {
              const jour = e.target.value;
              const dispo = !estTA
                ? HORAIRES_NORMAL
                : (JOURS_WEEKEND.includes(jour)
                  ? HORAIRES_TA.filter(h => h.index >= 2)
                  : HORAIRES_TA.filter(h => h.index <= 1));
              const periode = dispo.some(h => h.index === newCours.periode) ? newCours.periode : dispo[0].index;
              const duree = creneauValide(regime, jour, periode, newCours.duree) ? newCours.duree : 1;
              setNewCours({ ...newCours, jour, periode, duree });
            }}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}
          >
            {jours.map(j => <option key={j} value={j}>{j.charAt(0).toUpperCase() + j.slice(1)}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label>Horaire *</label>
          <select
            value={newCours.periode}
            onChange={(e) => {
              const periode = parseInt(e.target.value);
              const duree = creneauValide(regime, newCours.jour, periode, newCours.duree) ? newCours.duree : 1;
              setNewCours({ ...newCours, periode, duree });
            }}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}
          >
            {horairesFormulaire.map(h => <option key={h.index} value={h.index}>{h.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label>Durée *</label>
          <select
            value={newCours.duree}
            onChange={(e) => setNewCours({ ...newCours, duree: parseInt(e.target.value) })}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}
          >
            <option value={1}>1 séance (1h30)</option>
            <option value={2} disabled={!creneauValide(regime, newCours.jour, newCours.periode, 2)}>2 séances fusionnées (3h)</option>
          </select>
          {estTA && <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>3h possible uniquement à 19:00, 09:30 ou 13:30.</p>}
        </div>
              <div style={{ marginBottom: '15px', padding: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
<label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', marginBottom: filtreMatiereActif ? '10px' : '0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={filtreMatiereActif} onChange={(e) => setFiltreMatiereActif(e.target.checked)} />
                  Filtrer les matières par semestre
                </label>
                {filtreMatiereActif && (() => {
                  // Le niveau n'est pas demande : la page a deja une classe choisie en haut,
                  // et c elle qui determine le niveau. On se contente de l afficher pour que
                  // le programmeur verifie dans quelle filiere il travaille.
                  const c = classes.find(x => classeId(x) === String(filtreGroupeMatiere)) || classes.find(x => classeNom(x) === selectedClasse);
                  const niv = getNiveauCourant(c);
                  const fil = getFiliereDeNiveau(niv);
                  const classeDeRef = classeNom(c) || selectedClasse || '—';
                  // La comparaison large ramenait les periodes des deux IRM2 : meme code de niveau pour
      // l annee 1 et la copie 2027, les semestre s affichaient donc en double. On compare
      // sur l identifiant strict, puis on ne garde qu une periode par code de semestre :
      // la base contient plusieurs fois S1 et S2 pour un meme niveau.
      const periodesDuNiveau = niv
        ? [...filtrerParNiveau(periodes, niv, p => p.niveau_id)
            .filter((p, i, arr) => arr.findIndex(x => codeSemestre(x) === codeSemestre(p)) === i)]
        : getPeriodesDeClasse(c);
                  return (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1.4, minWidth: '220px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', fontWeight: '500' }}>Niveau</label>
                        <div style={{ padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', color: '#1f2937' }}>
                          {niv ? getLibelleNiveau(niv) : `Aucune correspondance pour ${classeDeRef}`}
                        </div>
                        <p style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '4px' }}>
                          Issu de la classe {classeDeRef}
                          {fil ? ` · filière ${fil.intitule || fil.nom || fil.code_filiere}` : ''}
                        </p>
                      </div>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', fontWeight: '500' }}>Semestre</label>
                        <select value={filtreSemestreMatiere} onChange={(e) => { setFiltreSemestreMatiere(e.target.value); setNewCours({ ...newCours, matiereId: '' }); }} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}>
                          <option value="all">Tous les semestres</option>
                          {periodesDuNiveau.map(p => <option key={p._id || p.id} value={p._id || p.id}>{codeSemestre(p) ? `${codeSemestre(p)} — ` : ''}{p.nom || p.code}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                })()}
              </div>
              {(() => {
                // Seance commune a des niveaux differents : CPI1 et L1 IRM n ont pas les memes
                // matieres. Une liste par classe, sinon le choix de l une/ecraserait l autre.
                if (classesOntNiveauxDifferents()) {
                  const cls = classesDuFormulaire();
                  const per = getPeriodeFiltre();
                  return (
                    <div style={{ marginBottom: '15px' }}>
                      <label>Matière par classe *</label>
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '2px 0 10px' }}>
                        Séance commune : chaque groupe suit sa propre matière.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {cls.map(c => {
                          const cid = classeId(c);
                          // le semestre choisi est celui de la classe de reference : on le
                          // translate vers la periode equivalente de CETTE classe, sinon on
                          // filtrerait ses matieres avec une periode qui n est pas la sienne
                          const codeFiltre = per ? codeSemestre(per) : '';
                          const perC = codeFiltre
                            ? getPeriodesDeClasse(c).find(p => codeSemestre(p) === codeFiltre)
                            : null;
                          const liste = getMatieresPourClasse(c, perC ? perC._id : '');
                          const niv = getNiveauCourant(c);
                          return (
                            <div key={cid}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', fontWeight: 600 }}>
                                {classeNom(c)}
                                <span style={{ fontWeight: 400, color: '#6b7280' }}>
                                  {niv ? ` · ${getLibelleNiveau(niv)}` : ''}
                                </span>
                              </label>
                              <select
                                value={(newCours.matieresParClasse || {})[cid] || ''}
                                onChange={(e) => setNewCours({
                                  ...newCours,
                                  matieresParClasse: { ...(newCours.matieresParClasse || {}), [cid]: e.target.value }
                                })}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}
                              >
                                <option value="">Sélectionner une matière</option>
                                {liste.map(m => {
                                  const mod = getModuleDeMatiere(m);
                                  const txt = m.code ? `${m.code} — ${m.name}` : m.name;
                                  return (
                                    <option key={m.id} value={m.id}>
                                      {mod && (mod.code || mod.nom) ? `${txt} (${mod.code || mod.nom})` : txt}
                                    </option>
                                  );
                                })}
                              </select>
                              <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '2px' }}>
                                {liste.length} matière(s) disponible(s)
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const { groupes, total, semestreIgnore, filiere, niveau, niveauDeclare, stats, diagnostic } = getGroupesMatieres(); const groupeNom = classes.find(c => classeId(c) === String(filtreGroupeMatiere)) || classes.find(c => classeNom(c) === selectedClasse); const per = getPeriodeFiltre();
                const diagnosticBloquant = diagnostic && diagnostic.deLAnnee === 0 && diagnostic.total > 0;
                return (
                <div style={{ marginBottom: '15px' }}>
                  <label>Matière *</label>
                  <select value={newCours.matiereId} onChange={(e) => setNewCours({...newCours, matiereId: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}>
                    <option value="">Sélectionner une matière</option>
                    {groupes.map(g => g.matieres.length > 0 && (
                      <optgroup key={g.code} label={filtreSemestreMatiere === 'all' ? `${g.label} (${g.matieres.length})` : undefined}>
                        {g.matieres.map(m => {
                          const mod = getModuleDeMatiere(m);
                          const txt = m.code ? `${m.code} — ${m.name}` : m.name;
                          return <option key={m.id} value={m.id}>{mod && (mod.code || mod.nom) ? `${txt} (${mod.code || mod.nom})` : txt}</option>;
                        })}
                      </optgroup>
                    ))}
                  </select>
                  {diagnosticBloquant && (
                    <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e', padding: '10px 12px', borderRadius: '8px', fontSize: '0.8rem', marginBottom: '10px' }}>
                      <strong>Aucune matière pour ce groupe en {anneeActive ? `${anneeActive}` : 'année active'}.</strong>
                      {' '}Le niveau {diagnostic.niveau.intitule} (id {diagnostic.niveau.niveau_id}) possède {diagnostic.total} matière(s),
                      {' '}toutes enregistrées pour l'année {diagnostic.autresAnnees.join(', ') || '—'}.
                      {diagnostic.autresAnnees.length > 0 && diagnostic.niveau.copiedFrom && (
                        <> Ce niveau est la copie d'un niveau d'une année précédente : il faut lui affecter les matières de {anneeActive}, ou rattacher la classe au niveau {diagnostic.niveau.copiedFrom}.</>
                      )}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: semestreIgnore ? '#b45309' : '#6b7280', marginTop: '4px' }}>
                    {semestreIgnore
                      ? `Aucune matière rattachée à ${per ? per.nom || per.code : filtreSemestreMatiere} — affichage des ${total} matière(s) du niveau`
                      : `${total} matière(s) affichée(s) — groupe ${classeNom(groupeNom) || '—'} / niveau id ${niveau ? niveau.niveau_id : '—'}${niveauDeclare && niveau && String(niveauDeclare.niveau_id) !== String(niveau.niveau_id) ? ` (la classe déclare le niveau ${niveauDeclare.niveau_id}, sans module : programme repris ici)` : ''} (année ${niveau && niveau.annee_id ? niveau.annee_id : 'nulle'})${diagnostic ? ` / ${diagnostic.deLAnnee} matière(s) sur l'année ${anneeActive || '—'}` : ''}${filiere ? ` / filière ${filiere.intitule || filiere.code_filiere || filiere.filiere_id}` : ''}${per ? ` / ${per.nom || per.code}` : ''}`}
                  </div>
                </div>
              ); })()}
              <div style={{ marginBottom: '15px' }}><label>Professeur</label><select value={newCours.professeurId} onChange={(e) => setNewCours({...newCours, professeurId: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}><option value="">Sélectionner un professeur</option>{professeurs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
              <div style={{ marginBottom: '15px' }}><label>Salle</label><select value={newCours.salleId} onChange={(e) => setNewCours({...newCours, salleId: e.target.value})} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}><option value="">Sélectionner une salle</option>{salles.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              
              {(() => {
                const classesDuFormulaire = normaliserClasses(newCours.classes, classes, selectedClasse);
                if (!newCours.professeurId && !newCours.salleId && classesDuFormulaire.length === 0) return null;

                // Une seance identique existe deja : les classes cochees vont y etre rattachees,
                // donc le prof et la salle ne sont pas en conflit, ils sont deja occupes par
                // ce meme cours. Annoncer un conflit ici serait un faux positif.
                const rattachement = !editingCours && trouverSeanceIdentique(
                  newCours.jour, newCours.periode, newCours.duree || 1, newCours.professeurId, newCours.salleId, regime
                );
                if (rattachement) {
                  return (
                    <div style={{ padding: '10px 12px', borderRadius: '8px', fontSize: '0.8125rem', marginBottom: '15px', background: '#ede9fe', color: '#5b21b6', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span>👥 Ces {classesDuFormulaire.length} classes seront regroupées sur la séance existante</span>
                      <span style={{ opacity: 0.8 }}>Le professeur et la salle restent inchangés.</span>
                    </div>
                  );
                }

                const c = presencesConflit(newCours.jour, newCours.periode, newCours.duree || 1, newCours.professeurId, newCours.salleId, editingCours ? editingCours.cardId : null, classesDuFormulaire);
                const profOK = !newCours.professeurId || c.prof.length === 0;
                const salleOK = !newCours.salleId || c.salle.length === 0;
                const classeOK = c.classe.length === 0;
                const toutOK = profOK && salleOK && classeOK;
                return (
                  <div style={{ padding: '10px 12px', borderRadius: '8px', fontSize: '0.8125rem', marginBottom: '15px', background: toutOK ? '#dcfce7' : '#fee2e2', color: toutOK ? '#166534' : '#b91c1c', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span>{profOK ? '✓ Prof disponible' : `✗ Prof non disponible (${c.prof[0]})`}</span>
                    <span>{salleOK ? '✓ Salle disponible' : `✗ Salle occupée (${c.salle[0]})`}</span>
                    {classesDuFormulaire.length > 1 && <span>{classeOK ? `✓ ${classesDuFormulaire.length} classes libres` : `✗ ${c.classe[0]}`}</span>}
                  </div>
                );
              })()}
              
              {/* Le bandeau general est dans le flux de la page, donc masque par la modale.
                  Sans cet affichage, tout refus de validation restait invisible et le bouton
                  semblait ne rien faire. */}
              {message && (
                <div style={{
                  background: message.includes('succès') ? '#dcfce7' : (message.startsWith('⚠️') || message.includes('Erreur')) ? '#fee2e2' : '#fef3c7',
                  color: message.includes('succès') ? '#166534' : (message.startsWith('⚠️') || message.includes('Erreur')) ? '#b91c1c' : '#92400e',
                  padding: '10px 12px', borderRadius: '8px', fontSize: '0.8125rem', marginBottom: '12px', fontWeight: '600'
                }}>
                  {message}
                </div>
              )}

              {(() => {
                const vaRegrouper = !editingCours && newCours.classes.length > 1 && trouverSeanceIdentique(
                  newCours.jour, newCours.periode, newCours.duree || 1, newCours.professeurId, newCours.salleId, regime
                );
                const libelle = editingCours ? 'Mettre à jour' : vaRegrouper ? '👥 Regrouper les classes' : 'Ajouter';
                const couleur = editingCours ? '#3b82f6' : vaRegrouper ? '#7c3aed' : '#8b5cf6';
                return (
                  <button onClick={editingCours ? handleUpdateCours : handleAddCours} style={{ width: '100%', padding: '12px', background: couleur, border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600', cursor: 'pointer', marginTop: '10px' }}>{libelle}</button>
                );
              })()}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'white', borderRadius: '12px', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
          <p>📊 {emploiData.length} cours programmés pour {selectedClasse}</p>
          <p>🏫 {salles.length} salles | 👨‍🏫 {professeurs.length} enseignants | 📚 {matieres.length} matières</p>
        </div>
      </div>
    </div>
  );
};

export default EmploiTemps;


